import { Component, OnInit } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { SlimLoadingBarService } from "ng2-slim-loading-bar";
import { ProjectsService } from "../services/projects/projects.service";
import { ErrorHandlerService } from "../services/shared/error-handler.service";
import { CeoReportsService } from "../services/ceo-reports/ceo-reports.service";
import { removeNullishFieldsParams } from "../shared/object_helpers";
import { FinancialReportsService } from "../services/financial-reports/financial-reports.service";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../services/i18n/language.service";
import { Router } from "@angular/router";

@Component({
    selector: "app-ceo-reports",
    templateUrl: "./ceo-reports.component.html",
    styleUrls: ["./ceo-reports.component.css"],
})
export class CeoReportsComponent implements OnInit {
  isLoading = false;
  lang: string = "en";

  totalTotalCount = 0;
  totalTotalArea = 0;
  totalTotalPrice = 0;
  totalTotalMaint = 0;
  totalTotalFinal = 0;

  totalSoldCount = 0;
  totalSoldArea = 0;
  totalSoldPrice = 0;
  totalSoldMaint = 0;
  totalSoldFinal = 0;

  totalUnsoldCount = 0;
  totalUnsoldArea = 0;
  totalUnsoldPrice = 0;
  totalUnsoldMaint = 0;
  totalUnsoldFinal = 0;

  view = "sold_inventorty";
  dues_inventorty_sub_view = "installment"; // Sub-tab for Project Over Due
  dues_payment_with_sharing_sub_view = "installment"; // Sub-tab for Project Over Due Partner
  page = 1;
  paginate;

  views_conf = {
    filters: false,
    charts: false,
  };

  // Chart configurations for investment tab
  investmentChartData: any = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: []
    }]
  };

  investmentChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  investmentChartType = 'pie';

  // Bar chart configuration for gross area comparison
  investmentBarChartData: any = {
    labels: [],
    datasets: [
      {
        label: 'Total Gross Area',
        data: [],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Sold Units Gross Area',
        data: [],
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }
    ]
  };

  investmentBarChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        scaleLabel: {
          display: true,
          labelString: 'Gross Area'
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45
        }
      }]
    },
    tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.yLabel;

          // If it's the sold units dataset (index 1), calculate and show percentage
          if (tooltipItem.datasetIndex === 1) {
            const totalGrossArea = data.datasets[0].data[tooltipItem.index];
            const percentage = totalGrossArea > 0 ? ((value / totalGrossArea) * 100).toFixed(2) : 0;
            return label + ': ' + value.toLocaleString() + ' (' + percentage + '%)';
          }

          return label + ': ' + value.toLocaleString();
        }
      }
    },
    legend: {
      position: 'top'
    }
  };

  investmentBarChartType = 'bar';

  // New Pie Chart for Sold vs Inventory Units Gross Area
  investmentGrossAreaPieChartData: any = {
    labels: ['Sold Units Gross Area', 'Inventory Units Gross Area'],
    datasets: [{
      data: [],
      backgroundColor: ['#36A2EB', '#FF6384'],
      hoverBackgroundColor: ['#36A2EB', '#FF6384']
    }]
  };

  investmentGrossAreaPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  investmentGrossAreaPieChartType = 'pie';

  // Developer Percentage Charts Configuration
  // Pie chart for collected values
  developerPieChartData: any = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: []
    }]
  };

  developerPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  developerPieChartType = 'pie';

  // Bar chart for all vs collected comparison
  developerBarChartData: any = {
    labels: [],
    datasets: [
      {
        label: 'All',
        data: [],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Collected',
        data: [],
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }
    ]
  };

  developerBarChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        scaleLabel: {
          display: true,
          labelString: 'Amount'
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45
        }
      }]
    },
    tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.yLabel;

          // If it's the collected dataset (index 1), calculate and show percentage
          if (tooltipItem.datasetIndex === 1) {
            const allAmount = data.datasets[0].data[tooltipItem.index];
            const percentage = allAmount > 0 ? ((value / allAmount) * 100).toFixed(2) : 0;
            return label + ': ' + value.toLocaleString() + ' (' + percentage + '%)';
          }

          return label + ': ' + value.toLocaleString();
        }
      }
    },
    legend: {
      position: 'top'
    }
  };

  developerBarChartType = 'bar';

  // New Overall Collected vs Remaining Pie Chart for Developer Percentage
  developerOverallPieChartData: any = {
    labels: ['Collected', 'Remaining'],
    datasets: [{
      data: [],
      backgroundColor: ['#36A2EB', '#FF6384'],
      hoverBackgroundColor: ['#36A2EB', '#FF6384']
    }]
  };

  developerOverallPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  developerOverallPieChartType = 'pie';

  // Installment Charts Configuration
  // Pie chart for collected values
  installmentPieChartData: any = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: []
    }]
  };

  installmentPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  installmentPieChartType = 'pie';

  // Bar chart for all vs collected comparison
  installmentBarChartData: any = {
    labels: [],
    datasets: [
      {
        label: 'All',
        data: [],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Collected',
        data: [],
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }
    ]
  };

  installmentBarChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        scaleLabel: {
          display: true,
          labelString: 'Amount'
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45
        }
      }]
    },
    tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.yLabel;

          // If it's the collected dataset (index 1), calculate and show percentage
          if (tooltipItem.datasetIndex === 1) {
            const allAmount = data.datasets[0].data[tooltipItem.index];
            const percentage = allAmount > 0 ? ((value / allAmount) * 100).toFixed(2) : 0;
            return label + ': ' + value.toLocaleString() + ' (' + percentage + '%)';
          }

          return label + ': ' + value.toLocaleString();
        }
      }
    },
    legend: {
      position: 'top'
    }
  };

  installmentBarChartType = 'bar';

  // New Overall Collected vs Remaining Pie Chart
  installmentOverallPieChartData: any = {
    labels: ['Collected', 'Remaining'],
    datasets: [{
      data: [],
      backgroundColor: ['#36A2EB', '#FF6384'],
      hoverBackgroundColor: ['#36A2EB', '#FF6384']
    }]
  };

  installmentOverallPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  installmentOverallPieChartType = 'pie';

  // Division Report Charts Configuration
  // Bar chart for Direct/Indirect/Total
  divisionBarChartData: any = {
    labels: [],
    datasets: [
      {
        label: 'Direct',
        data: [],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Indirect',
        data: [],
        backgroundColor: 'rgba(255, 206, 86, 0.8)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      },
      {
        label: 'Total',
        data: [],
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  divisionBarChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        scaleLabel: {
          display: true,
          labelString: 'Total Price'
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45
        }
      }]
    },
    tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.yLabel;
          return label + ': ' + value.toLocaleString();
        }
      }
    },
    legend: {
      position: 'top'
    }
  };

  divisionBarChartType = 'bar';

  // Pie chart for Direct vs Indirect
  divisionPieChartData: any = {
    labels: ['Direct', 'Indirect'],
    datasets: [{
      data: [],
      backgroundColor: ['#36A2EB', '#FFCE56'],
      hoverBackgroundColor: ['#36A2EB', '#FFCE56']
    }]
  };

  divisionPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    }
  };

  divisionPieChartType = 'pie';

  // Sold Inventory Chart Configuration
  // Pie chart for developer percentage
  soldInventoryPieChartData: any = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: []
    }]
  };

  soldInventoryPieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'right',
      labels: {
        generateLabels: (chart) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const dataset = data.datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(2);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: dataset.backgroundColor[i],
                hidden: false,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const currentValue = dataset.data[tooltipItem.index];
          const total = dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((currentValue / total) * 100).toFixed(2);
          const label = data.labels[tooltipItem.index];
          return `${label}: ${currentValue.toLocaleString()} (${percentage}%)`;
        }
      }
    },
    title: {
      display: true,
      text: "",
      fontSize: 16
    }
  };

  soldInventoryPieChartType = 'pie';

  reports: any = {
    floor_analysis_report: null,
    floor_analysis_report_special: null,
    collection_remaining_amount_balance: {
      data: [],
      raw_response: null,
      totals: {
        amount: 0,
        amount_received: 0,
        remaining_amount: 0,
      },
    },
    collection_remaining_amount_balance_special: {
      data: [],
      raw_response: null,
      totals: {
        amount: 0,
        amount_received: 0,
        remaining_amount: 0,
      },
    },
    sold_inventorty: {
      data: [],
      total: {},
    },
    sold_inventorty_special: {
      data: [],
      total: {},
    },
    avaliable_inventorty: {
      data: [],
      total: {},
    },
    dues_payment_with_sharing: {
      data: [],
      total: {},
    },
    dues_inventorty: {
      data: [],
      total: {},
    },
    not_avaliable_inventorty: {
      data: [],
      total: {},
    },
    monthly_payments_analysis: {},
    monthly_payments_analysis_total: null,
    partner_monthly_payments_analysis: {},
    partner_monthly_payments_analysis_total: null,
    investment: {
      data: [],
      raw_response: null,
      totals: null,
    },
    developer_percentage: {
      data: [],
      raw_response: null,
      totals: null,
    },
    installment: {
      data: [],
      raw_response: null,
      totals: null,
    },
    division_report: null,
  };

  filter_form: any = {};
  filter_schema: any = {};

  filter_dependencies = {
    projects: [],
    unitTypes: [],
    banks: [],
    years: Array(50)
      .fill("")
      .map((v, idx) => new Date().getUTCFullYear() - idx + 25),
    deal_statuses: ["all", "contracted", "pending"],
    project_types: ["Residential", "Commercial"],
    payment_types: ["fully-collected", "pending"],
    reservation_statuses: [
      { label: "Active", value: "active" },
      { label: "Cancelled", value: "cancelled" },
    ],
  };
  downloading_export: any = false;
  monthsArray = Array.from({ length: 13 }, (_, i) => i);

  constructor(
    private projectService: ProjectsService,
    private financialReportsService: FinancialReportsService,
    private errorHandlerService: ErrorHandlerService,
    private ceoReportsService: CeoReportsService,
    private slimLoadingBarService: SlimLoadingBarService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private router: Router
  ) {
    this.lang = this.languageService.getCurrentLang();
  }

  ngOnInit() {
    this.translate.onLangChange.subscribe(() => {
      this.lang = this.languageService.getCurrentLang();
      this.setChartTitles();
    });
    this.setChartTitles();
    this.setView(this.view);
    this.getProjects();
  }

  switchLang(lang: string) {
    this.languageService.use(lang);
    this.lang = this.languageService.getCurrentLang();
    this.setChartTitles();
  }

  private setChartTitles() {
    if (this.soldInventoryPieChartOptions && this.soldInventoryPieChartOptions.title) {
      this.soldInventoryPieChartOptions.title.text = this.translate.instant(
        "ceo_reports.developer_percentage_distribution"
      );
    }
  }

  getProjects(params: any = {}) {
    this.projectService.getProjects(params).subscribe((res: any) => {
      this.filter_dependencies.projects = Array.isArray(res.data) ? res.data : [];
    });
  }

  setView(tab: string) {
    this.view = tab;
    this.filter_form = {};
    this.views_conf.filters = false;
    this.views_conf.charts = null;
    switch (tab) {
      case "sold_inventorty":
      case "sold_inventorty_special": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.views_conf.charts = true;
        this.getProjects();
        break;
      }
      case "collection_remaining_amount_balance":
      case "collection_remaining_amount_balance_special": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
          filter_year: 1,
        };
        this.getProjects();
        break;
      }
      case "avaliable_inventorty": {
        this.filter_schema = {
          filter_project_id: 1,
        };
        this.getProjects();
        break;
      }
      case "dues_payment_with_sharing": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.dues_payment_with_sharing_sub_view = "installment"; // Reset to default sub-view
        this.getProjects();
        break;
      }
      case "dues_inventorty": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.dues_inventorty_sub_view = "installment"; // Reset to default sub-view
        this.getProjects();
        break;
      }
      case "not_avaliable_inventorty": {
        this.filter_schema = {
          filter_project_id: 1,
        };
        this.getProjects();
        break;
      }
      case "monthly_payments_analysis": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.getProjects({ only_has_partner: true });
        break;
      }
      case "partner_monthly_payments_analysis": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.getProjects();
        break;
      }
      case "investment": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.views_conf.charts = true;
        this.getProjects();
        break;
      }
      case "developer_percentage": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.views_conf.charts = true;
        this.getProjects();
        break;
      }
      case "installment": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.views_conf.charts = true;
        this.getProjects();
        break;
      }
      case "division_report": {
        this.filter_schema = {
          filter_project_id: 1,
          filter_date_from: 1,
          filter_date_to: 1,
        };
        this.views_conf.charts = true;
        this.view = tab;
        break;
      }
      case "floor_analysis_report":
      case "floor_analysis_report_special": {
        this.filter_schema = {
          filter_project_id: 1,
        };
        this.getProjects();
        break;
      }
    }

    this.getData();
  }

  getData() {
    this.page = 1;
    switch (this.view) {
      case "sold_inventorty": {
        this.getSoldInventortyReport();
        break;
      }
      case "sold_inventorty_special": {
        this.getSoldInventortyReport(false, true);
        break;
      }
      case "collection_remaining_amount_balance": {
        this.getCollectionRemainingAmountBalanceReport();
        break;
      }
      case "collection_remaining_amount_balance_special": {
        this.getCollectionRemainingAmountBalanceReport(false, true);
        break;
      }
      case "avaliable_inventorty": {
        this.getAvaliableInventortyReport();
        break;
      }
      case "dues_payment_with_sharing": {
        this.getDuesPaymentWithSharingReport();
        break;
      }
      case "dues_inventorty": {
        this.getDuesInventoryReport();
        break;
      }
      case "not_avaliable_inventorty": {
        this.getNotAvaliableInventortyReport();
        break;
      }
      case "monthly_payments_analysis": {
        this.getMonthlyPaymentsReport();
        break;
      }
      case "partner_monthly_payments_analysis": {
        this.getPartnerMonthlyPaymentsReport();
        break;
      }
      case "investment": {
        this.getInvestmentReport();
        break;
      }
      case "developer_percentage": {
        this.getDeveloperPercentageReport();
        break;
      }
      case "installment": {
        this.getInstallmentReport();
        break;
      }
      case "division_report": {
        this.getDivisionReport();
        break;
      }
      case "floor_analysis_report": {
        this.getFloorAnalysisReport();
        break;
      }
      case "floor_analysis_report_special": {
        this.getFloorAnalysisReport(true);
        break;
      }
    }
  }

  getFloorAnalysisReport(includeSpecialReservations?: boolean) {
    this.isLoading = true;
    this.slimLoadingBarService.start();

    const params: any = { ...this.filter_form };
    if (params.project_id) {
      params.projects_ids = params.project_id;
    }
    if (includeSpecialReservations || this.view === "floor_analysis_report_special") {
      params.include_special_reservations = true;
    }

    const totalReq = this.projectService.getTotalUnitReport(params);
    const soldReq = this.projectService.getSoldReservationReport(params);
    const unsoldReq = this.projectService.getUnsoldUnitReport(params);

    Observable.forkJoin([totalReq, soldReq, unsoldReq]).subscribe(
      (results: any[]) => {
        const [totalRes, soldRes, unsoldRes] = results;
        const merged = this.mergeReports(
          totalRes,
          soldRes,
          unsoldRes
        );
        if (this.view === "floor_analysis_report_special") {
          this.reports.floor_analysis_report_special = merged;
        } else {
          this.reports.floor_analysis_report = merged;
        }
        this.isLoading = false;
        this.slimLoadingBarService.complete();
      },
      (err) => {
        console.log(err);
        this.isLoading = false;
        this.slimLoadingBarService.complete();
        this.errorHandlerService.handleErorr(err);
      }
    );
  }

  mergeReports(totalRes: any[], soldRes: any[], unsoldRes: any[]) {
    const mergedMap = new Map();

    const getEntry = (projectName, typeName, floorName) => {
      const key = `${projectName}_${typeName}_${floorName}`;
      if (!mergedMap.has(key)) {
        mergedMap.set(key, {
          project_name: projectName,
          type_name: typeName,
          floor_name: floorName,

          total_count: 0,
          total_area: 0,
          total_price: 0,
          total_maint: 0,
          total_total_with_maint: 0,

          sold_count: 0,
          sold_percentage: 0,
          sold_area: 0,
          sold_price: 0,
          sold_maint: 0,
          sold_total_with_maint: 0,

          unsold_count: 0,
          unsold_percentage: 0,
          unsold_area: 0,
          unsold_price: 0,
          unsold_maint: 0,
          unsold_total_with_maint: 0,
        });
      }
      return mergedMap.get(key);
    };

    // Helper to ensure array
    const toArray = (data: any) =>
      Array.isArray(data) ? data : data ? [data] : [];

    // 1. Process Total Report
    toArray(totalRes).forEach((project) => {
      if (project.unit_types) {
        project.unit_types.forEach((type) => {
          if (type.floors) {
            type.floors.forEach((floor) => {
              const entry = getEntry(
                project.project_name,
                type.unit_type_name,
                floor.floor_number
              );
              entry.total_count = floor.total_units || 0;
              entry.total_area = floor.build_area_sum || 0;
              entry.total_price = floor.selling_price_sum || 0;
              entry.total_maint = floor.maintenance_sum || 0;
              entry.total_total_with_maint = floor.total_with_maintenance || 0;
            });
          }
        });
      }
    });

    // 2. Process Sold Report
    toArray(soldRes).forEach((project) => {
      if (project.unit_types) {
        project.unit_types.forEach((type) => {
          if (type.floors) {
            type.floors.forEach((floor) => {
              const entry = getEntry(
                project.project_name,
                type.unit_type_name,
                floor.floor_number
              );
              entry.sold_count = floor.sold_units || 0;
              entry.sold_area = floor.build_area_sum || 0;
              entry.sold_price = floor.selling_price_sum || 0;
              entry.sold_maint = floor.maintenance_sum || 0;
              entry.sold_total_with_maint = floor.total_with_maintenance || 0;
            });
          }
        });
      }
    });

    // 3. Process Unsold Report
    toArray(unsoldRes).forEach((project) => {
      if (project.unit_types) {
        project.unit_types.forEach((type) => {
          if (type.floors) {
            type.floors.forEach((floor) => {
              const entry = getEntry(
                project.project_name,
                type.unit_type_name,
                floor.floor_number
              );
              entry.unsold_count = floor.unsold_units || 0;
              entry.unsold_area = floor.build_area_sum || 0;
              entry.unsold_price = floor.selling_price_sum || 0;
              entry.unsold_maint = floor.maintenance_sum || 0;
              entry.unsold_total_with_maint = floor.total_with_maintenance || 0;
            });
          }
        });
      }
    });

    this.totalTotalCount = 0;
    this.totalTotalArea = 0;
    this.totalTotalPrice = 0;
    this.totalTotalMaint = 0;
    this.totalTotalFinal = 0;

    this.totalSoldCount = 0;
    this.totalSoldArea = 0;
    this.totalSoldPrice = 0;
    this.totalSoldMaint = 0;
    this.totalSoldFinal = 0;

    this.totalUnsoldCount = 0;
    this.totalUnsoldArea = 0;
    this.totalUnsoldPrice = 0;
    this.totalUnsoldMaint = 0;
    this.totalUnsoldFinal = 0;

    const types = Array.from(mergedMap.values());
    types.forEach((item: any) => {
      // Calculate Percentages
      if (item.total_count > 0) {
        item.sold_percentage = Math.round(
          (item.sold_count / item.total_count) * 100
        );
        item.unsold_percentage = Math.round(
          (item.unsold_count / item.total_count) * 100
        );
      } else {
        // Fallback if total_count is missing
        const computedTotal = item.sold_count + item.unsold_count;
        if (computedTotal > 0) {
          item.sold_percentage = Math.round(
            (item.sold_count / computedTotal) * 100
          );
          item.unsold_percentage = Math.round(
            (item.unsold_count / computedTotal) * 100
          );
        }
      }

      this.totalTotalCount += item.total_count;
      this.totalTotalArea += item.total_area;
      this.totalTotalPrice += item.total_price;
      this.totalTotalMaint += item.total_maint;
      this.totalTotalFinal += item.total_total_with_maint;

      this.totalSoldCount += item.sold_count;
      this.totalSoldArea += item.sold_area;
      this.totalSoldPrice += item.sold_price;
      this.totalSoldMaint += item.sold_maint;
      this.totalSoldFinal += item.sold_total_with_maint;

      this.totalUnsoldCount += item.unsold_count;
      this.totalUnsoldArea += item.unsold_area;
      this.totalUnsoldPrice += item.unsold_price;
      this.totalUnsoldMaint += item.unsold_maint;
      this.totalUnsoldFinal += item.unsold_total_with_maint;
    });

    return { types };
  }

  getSoldInventortyReport(is_export?, includeSpecialReservations?: boolean) {
    this.slimLoadingBarService.start();
    const isSpecial = includeSpecialReservations || this.view === "sold_inventorty_special";
    const payloadParams: any = { ...this.filter_form };
    if (isSpecial) {
      payloadParams.include_special_reservations = true;
    }

    if (is_export) {
      this.downloading_export = 1;
      payloadParams.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService.getSoldInventortyReport(payloadParams).subscribe(
      (res: any) => {
        if (is_export) {
          window.open(res.url);
          this.downloading_export = false;
          this.filter_form.is_export = false;
          delete this.filter_form.type;
        } else {
          const reportObj = isSpecial ? this.reports.sold_inventorty_special : this.reports.sold_inventorty;
          reportObj.data = res.data;
          reportObj.total = res.total;

          // Prepare chart data for sold inventory
          this.prepareSoldInventoryChart(isSpecial);
        }
      },
      (err) => {
        this.downloading_export = false;
        this.errorHandlerService.handleErorr(err);
      },
      () => {
        if (this.view === "sold_inventorty" || this.view === "sold_inventorty_special") {
          this.slimLoadingBarService.complete();
          this.disableLoading();
        }
      },
    );
  }

  getAvaliableInventortyReport(is_export?) {
    this.slimLoadingBarService.start();
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService
      .getAvaliableInventortyReport(this.filter_form)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
          } else {
            this.reports.avaliable_inventorty.data = res.data;
            this.reports.avaliable_inventorty.total = res.total;
          }
        },
        (err) => {
          this.downloading_export = false;
          this.errorHandlerService.handleErorr(err);
        },
        () => {
          this.slimLoadingBarService.complete();
          if (this.view === "avaliable_inventorty") {
            this.slimLoadingBarService.complete();
            this.disableLoading();
          }
        },
      );
  }

  setDuesPaymentWithSharingSubView(subView: string) {
    this.dues_payment_with_sharing_sub_view = subView;
    this.getDuesPaymentWithSharingReport();
  }

  getDuesPaymentWithSharingReport(is_export?) {
    this.slimLoadingBarService.start();
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }

    // Add payment_ids filter based on sub-view
    const filterParams = { ...this.filter_form };
    if (this.dues_payment_with_sharing_sub_view === 'down_payment') {
      // Down payment: payment_ids[]=1
      filterParams['payment_ids[]'] = [1];
    } else if (this.dues_payment_with_sharing_sub_view === 'installment') {
      // Installment: payment_ids[]=2&payment_ids[]=3
      filterParams['payment_ids[]'] = [2, 3];
    }

    this.ceoReportsService
      .getDuesPaymentWithSharing(filterParams)
      .subscribe(
        (res: any) => {
          this.reports.dues_payment_with_sharing.data = res.projects;
          this.reports.dues_payment_with_sharing.total = res;
        },
        (err) => {
          this.errorHandlerService.handleErorr(err);
        },
        () => {
          this.slimLoadingBarService.complete();
          if (this.view === "dues_payment_with_sharing") {
            this.disableLoading();
          }
        },
      );
  }

  setDuesInventortySubView(subView: string) {
    this.dues_inventorty_sub_view = subView;
    this.getDuesInventoryReport();
  }

  getDuesInventoryReport(is_export?) {
    this.slimLoadingBarService.start();
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }

    // Add payment_ids filter based on sub-view
    const filterParams = { ...this.filter_form };
    if (this.dues_inventorty_sub_view === 'down_payment') {
      // Down payment: payment_ids[]=1
      filterParams['payment_ids[]'] = [1];
    } else if (this.dues_inventorty_sub_view === 'installment') {
      // Installment: payment_ids[]=2&payment_ids[]=3
      filterParams['payment_ids[]'] = [2, 3];
    }

    this.ceoReportsService.getDuesInventoryReport(filterParams).subscribe(
      (res: any) => {
        if (is_export) {
          window.open(res.url);
          this.downloading_export = false;
          this.filter_form.is_export = false;
          delete this.filter_form.type;
        } else {
          this.reports.dues_inventorty.data = res.projects;
          this.reports.dues_inventorty.total = res.total_amount;
        }
      },
      (err) => {
        this.downloading_export = false;
        this.errorHandlerService.handleErorr(err);
      },
      () => {
        this.slimLoadingBarService.complete();
        if (this.view === "dues_inventorty") {
          this.slimLoadingBarService.complete();
          this.disableLoading();
        }
      },
    );
  }

  getNotAvaliableInventortyReport(is_export?) {
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService
      .getNotAvaliableInventortyReport(this.filter_form)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
          } else {
            this.reports.not_avaliable_inventorty.data = res.data;
            this.reports.not_avaliable_inventorty.total = res.total;
            console.log(this.reports.not_avaliable_inventorty);
          }
        },
        (err) => {
          this.downloading_export = false;
          this.errorHandlerService.handleErorr(err);
        },
        () => {
          this.slimLoadingBarService.complete();
          if (this.view === "not_avaliable_inventorty") {
            this.disableLoading();
          }
        },
      );
  }

  private buildCollectionRemainingAmountBalanceParams() {
    const params: any = { ...this.filter_form };
    const projectIds =
      params.project_id ||
      params["project_id[]"] ||
      params["filter_project_id[]"];
    const queryParams: any = {};

    if (projectIds !== undefined && projectIds !== null && projectIds !== "") {
      queryParams.project_id = projectIds;
    }

    if (params.filter_year !== undefined && params.filter_year !== null && params.filter_year !== "") {
      queryParams.year = params.filter_year;
    }

    if (params.filter_date_from) {
      queryParams.date_from = params.filter_date_from;
    }

    if (params.filter_date_to) {
      queryParams.date_to = params.filter_date_to;
    }

    if (params.is_export) {
      queryParams.is_export = params.is_export;
    }

    return queryParams;
  }

  getCollectionRemainingAmountBalanceReport(is_export?, includeSpecialReservations?: boolean) {
    const isSpecial = includeSpecialReservations || this.view === "collection_remaining_amount_balance_special";
    const params = this.buildCollectionRemainingAmountBalanceParams();
    if (isSpecial) {
      params.include_special_reservations = true;
    }

    if (is_export) {
      this.downloading_export = 1;
      params.is_export = 1;
    } else {
      this.enableLoading();
    }

    this.ceoReportsService
      .getCollectionRemainingAmountBalanceReport(params)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
            return;
          }

          const normalized = this.normalizeCollectionRemainingAmountBalanceReport(
            res
          );

          const targetReport = isSpecial
            ? this.reports.collection_remaining_amount_balance_special
            : this.reports.collection_remaining_amount_balance;

          targetReport.raw_response = res;
          targetReport.data = normalized.data.map((yearRow) => ({
            ...yearRow,
            expanded: yearRow.expanded !== undefined ? yearRow.expanded : false,
          }));
          targetReport.totals = normalized.totals;
        },
        (err) => {
          const targetReport = isSpecial
            ? this.reports.collection_remaining_amount_balance_special
            : this.reports.collection_remaining_amount_balance;

          this.downloading_export = false;
          targetReport.raw_response = err;
          targetReport.data = [];
          targetReport.totals = {
            amount: 0,
            amount_received: 0,
            remaining_amount: 0,
          };
          this.disableLoading();
          this.errorHandlerService.handleErorr(err);
        },
        () => {
          if (this.view === "collection_remaining_amount_balance" || this.view === "collection_remaining_amount_balance_special") {
            this.slimLoadingBarService.complete();
            this.disableLoading();
          }
        }
      );
  }

  normalizeCollectionRemainingAmountBalanceReport(raw: any) {
    const toNumber = (value: any) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const monthOrder = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];

    const monthLabelForIndex = (index: number) =>
      this.translate.instant(`ceo_reports.months.${monthOrder[index]}`) ||
      monthOrder[index];

    const monthIndexFromValue = (value: any) => {
      if (value === null || value === undefined || value === "") {
        return -1;
      }

      if (typeof value === "number" && value >= 1 && value <= 12) {
        return value - 1;
      }

      const normalized = String(value).toLowerCase().trim();
      if (/^\d{4}-\d{2}/.test(normalized)) {
        return Number(normalized.slice(5, 7)) - 1;
      }

      if (/^\d+$/.test(normalized)) {
        const parsed = Number(normalized);
        return parsed >= 1 && parsed <= 12 ? parsed - 1 : -1;
      }

      const lookup: any = {
        january: 0,
        jan: 0,
        february: 1,
        feb: 1,
        march: 2,
        mar: 2,
        april: 3,
        apr: 3,
        may: 4,
        june: 5,
        jun: 5,
        july: 6,
        jul: 6,
        august: 7,
        aug: 7,
        september: 8,
        sep: 8,
        sept: 8,
        october: 9,
        oct: 9,
        november: 10,
        nov: 10,
        december: 11,
        dec: 11,
      };

      return lookup[normalized] !== undefined ? lookup[normalized] : -1;
    };

    const normalizeText = (value: any) =>
      value === null || value === undefined ? "" : String(value).toLowerCase().trim();

    const getPaymentTypeKey = (item: any) =>
      normalizeText(
        (item && item.payment_type) ||
          (item && item.payment_type_name) ||
          (item && item.payment_type_text) ||
          (item && item.paymentType) ||
          (item && item.payment_type_id) ||
          (item && item.type)
      );

    const getStatusKey = (item: any) =>
      normalizeText(
        (item && item.status) ||
          (item && item.payment_status) ||
          (item && item.collection_status)
      );

    const isCdbRecord = (item: any) => {
      const paymentTypeKey = getPaymentTypeKey(item);
      return paymentTypeKey === "cdb" || paymentTypeKey === "5";
    };

    const receivedStatuses = new Set([
      "fully-collected",
      "fully collected",
      "pending-collection",
      "pending collect",
    ]);

    const remainingStatuses = new Set([
      "under-collection",
      "under collection",
      "bounced",
    ]);

    const isReceivedStatus = (item: any) => receivedStatuses.has(getStatusKey(item));
    const isRemainingStatus = (item: any) => remainingStatuses.has(getStatusKey(item));

    const getAmountValue = (item: any) =>
      toNumber(
        (item && item.amount) ||
          (item && item.total_amount) ||
          (item && item.total) ||
          (item && item.value)
      );

    const getReceivedValue = (item: any) => {
      const explicitValue = toNumber(
        (item && item.amount_received) ||
          (item && item.collected_amount) ||
          (item && item.received_amount) ||
          (item && item.paid_amount)
      );
      const hasStatus = !!getStatusKey(item);

      if (hasStatus) {
        return isReceivedStatus(item) ? getAmountValue(item) : 0;
      }

      return explicitValue;
    };

    const getRemainingValue = (item: any) => {
      const explicitValue = toNumber(
        (item && item.remaining_amount) ||
          (item && item.under_amount) ||
          (item && item.balance_amount) ||
          (item && item.remain_amount)
      );
      const hasStatus = !!getStatusKey(item);

      if (hasStatus) {
        return isRemainingStatus(item) ? getAmountValue(item) : 0;
      }

      return explicitValue;
    };

    const createEmptyMonths = () =>
      Array.from({ length: 12 }, (_, index) => ({
        month_index: index,
        month_name: monthLabelForIndex(index),
        amount: 0,
        amount_received: 0,
        remaining_amount: 0,
        queryParams: null,
      }));

    const yearsMap = new Map<string, any>();

    const ensureYear = (yearValue: any) => {
      const yearKey = String(yearValue);
      if (!yearsMap.has(yearKey)) {
        yearsMap.set(yearKey, {
          year: yearKey,
          months: createEmptyMonths(),
          amount: 0,
          amount_received: 0,
          remaining_amount: 0,
        });
      }
      return yearsMap.get(yearKey);
    };

    const addMonthTotals = (yearEntry: any, monthIndex: number, item: any) => {
      if (monthIndex < 0 || !yearEntry.months[monthIndex] || isCdbRecord(item)) {
        return;
      }

      yearEntry.months[monthIndex].amount += getAmountValue(item);
      yearEntry.months[monthIndex].amount_received += getReceivedValue(item);
      yearEntry.months[monthIndex].remaining_amount += getRemainingValue(item);
    };

    const addRowTotals = (yearEntry: any, item: any) => {
      if (isCdbRecord(item)) {
        return;
      }

      yearEntry.amount += getAmountValue(item);
      yearEntry.amount_received += getReceivedValue(item);
      yearEntry.remaining_amount += getRemainingValue(item);
    };

    const pushFlatRecord = (record: any) => {
      if (!record) {
        return;
      }

      const dateValue =
        record.date ||
        record.payment_date ||
        record.collect_date ||
        record.collection_date ||
        record.created_at ||
        record.db_date ||
        record.year_month ||
        record.month_date;
      const yearValue =
        record.year ||
        record.year_name ||
        (dateValue ? new Date(dateValue).getFullYear() : null);

      if (!yearValue) {
        return;
      }

      const yearEntry = ensureYear(yearValue);
      const monthValue =
        record.month != null
          ? record.month
          : record.month_index != null
          ? record.month_index
          : record.month_number != null
          ? record.month_number
          : dateValue
          ? new Date(dateValue).getMonth() + 1
          : null;
      const monthIndex = monthIndexFromValue(monthValue);

      addMonthTotals(yearEntry, monthIndex, record);
      addRowTotals(yearEntry, record);
    };

    const rawData = Array.isArray(raw)
      ? raw
      : (raw && raw.data) || (raw && raw.years) || (raw && raw.records) || [];

    if (Array.isArray(rawData)) {
      rawData.forEach((item) => {
        if (item && (item.months || item.data)) {
          const yearValue = item.year || item.year_name || item.label || item.name;
          if (!yearValue) {
            return;
          }

          const yearEntry = ensureYear(yearValue);
          const monthsSource = item.months || item.data || [];

          if (Array.isArray(monthsSource)) {
            monthsSource.forEach((monthItem) => {
              const monthIndex = monthIndexFromValue(
                monthItem.month != null
                  ? monthItem.month
                  : monthItem.month_index != null
                  ? monthItem.month_index
                  : monthItem.month_number != null
                  ? monthItem.month_number
                  : monthItem.month_name
              );
              addMonthTotals(yearEntry, monthIndex, monthItem);
              addRowTotals(yearEntry, monthItem);
            });
          } else if (monthsSource && typeof monthsSource === "object") {
            Object.keys(monthsSource).forEach((monthKey) => {
              const monthIndex = monthIndexFromValue(monthKey);
              addMonthTotals(yearEntry, monthIndex, monthsSource[monthKey]);
              addRowTotals(yearEntry, monthsSource[monthKey]);
            });
          }
          return;
        }

        pushFlatRecord(item);
      });
    } else if (rawData && typeof rawData === "object") {
      Object.keys(rawData).forEach((yearKey) => {
        if (yearKey === "total_for_total") {
          return;
        }

        const yearItem = rawData[yearKey];
        const yearEntry = ensureYear(yearKey);

        if (Array.isArray(yearItem)) {
          yearItem.forEach((monthItem, index) => {
            const monthIndex = monthIndexFromValue(
              monthItem.month != null
                ? monthItem.month
                : monthItem.month_index != null
                ? monthItem.month_index
                : index + 1
            );
            addMonthTotals(yearEntry, monthIndex, monthItem);
            addRowTotals(yearEntry, monthItem);
          });
          return;
        }

        if (yearItem && typeof yearItem === "object") {
          if (yearItem.months || yearItem.data) {
            const nested = yearItem.months || yearItem.data || {};
            if (Array.isArray(nested)) {
              nested.forEach((monthItem, index) => {
                const monthIndex = monthIndexFromValue(
                  monthItem.month != null
                    ? monthItem.month
                    : monthItem.month_index != null
                    ? monthItem.month_index
                    : index + 1
                );
                addMonthTotals(yearEntry, monthIndex, monthItem);
                addRowTotals(yearEntry, monthItem);
              });
            } else {
              Object.keys(nested).forEach((monthKey) => {
                const monthIndex = monthIndexFromValue(monthKey);
                addMonthTotals(yearEntry, monthIndex, nested[monthKey]);
                addRowTotals(yearEntry, nested[monthKey]);
              });
            }
            return;
          }

          addRowTotals(yearEntry, yearItem);
        }
      });
    }

    const data = Array.from(yearsMap.values())
      .sort((a, b) => Number(b.year) - Number(a.year))
      .map((yearEntry) => ({
        ...yearEntry,
        months: yearEntry.months.map((monthEntry, index) => ({
          ...monthEntry,
          month_name: monthEntry.month_name || monthLabelForIndex(index),
          queryParams: {
            filter_year: yearEntry.year,
            filter_month: index + 1,
          },
        })),
      }));

    const totals = data.reduce(
      (acc, item) => {
        acc.amount += item.amount || 0;
        acc.amount_received += item.amount_received || 0;
        acc.remaining_amount += item.remaining_amount || 0;
        return acc;
      },
      {
        amount: 0,
        amount_received: 0,
        remaining_amount: 0,
      }
    );

    const rawTotals =
      (raw && raw.totals) || (raw && raw.total) || (raw && raw.summary) || null;
    if (rawTotals && !data.length) {
      totals.amount = toNumber(
        rawTotals.amount || rawTotals.total_amount || totals.amount
      );
      totals.amount_received = toNumber(
        rawTotals.amount_received ||
          rawTotals.collected_amount ||
          rawTotals.received_amount ||
          totals.amount_received
      );
      totals.remaining_amount = toNumber(
        rawTotals.remaining_amount ||
          rawTotals.under_amount ||
          rawTotals.balance_amount ||
          totals.remaining_amount
      );
    }

    return {
      data,
      totals,
    };
  }

  toggleCollectionBalanceYear(yearRow: any) {
    yearRow.expanded = !yearRow.expanded;
  }

  private getCollectionBalanceRedirectStatuses(metric?: string) {
    switch (metric) {
      case "amount":
        return [
          "bounced",
          "under-collection",
          "fully-collected",
          "pending-collection",
        ];
      case "amount_received":
        return ["fully-collected", "pending-collection"];
      case "remaining_amount":
        return ["under-collection", "bounced"];
      default:
        return [];
    }
  }

  navigateToPaymentCollections(
    yearRow: any,
    monthRow?: any,
    overrideMonth?: number,
    metric?: string
  ) {
    const queryParams: any = {};

    const projectIds = this.filter_form["filter_project_id[]"];
    if (projectIds && projectIds.length) {
      queryParams["filter_project_id[]"] = projectIds;
    }

    // Pass reservation status filter to payment-collections (defaults to all for special report, active for normal report)
    const isSpecial = this.view === "collection_remaining_amount_balance_special";
    const reservationStatus =
      this.filter_form["filter_reservation_status"] !== undefined &&
      this.filter_form["filter_reservation_status"] !== null
        ? this.filter_form["filter_reservation_status"]
        : isSpecial
        ? ""
        : "active";
    queryParams["filter_reservation_status"] = reservationStatus;

    const statuses = this.getCollectionBalanceRedirectStatuses(metric);
    if (statuses.length) {
      queryParams["filter_status[]"] = statuses;
    }

    const year = Number(yearRow && yearRow.year);
    const monthIndex =
      overrideMonth !== undefined && overrideMonth !== null
        ? overrideMonth
        : monthRow && monthRow.month_index !== undefined
        ? monthRow.month_index
        : null;

    const formatDate = (date: Date) => {
      const yearPart = date.getFullYear();
      const monthPart = ("0" + (date.getMonth() + 1)).slice(-2);
      const dayPart = ("0" + date.getDate()).slice(-2);
      return `${yearPart}-${monthPart}-${dayPart}`;
    };

    if (year && monthIndex !== null && monthIndex !== undefined) {
      const startDate = new Date(year, monthIndex, 1);
      const endDate = new Date(year, monthIndex + 1, 0);
      queryParams.filter_date_from = formatDate(startDate);
      queryParams.filter_date_to = formatDate(endDate);
    } else {
      if (this.filter_form.filter_date_from) {
        queryParams.filter_date_from = this.filter_form.filter_date_from;
      }
      if (this.filter_form.filter_date_to) {
        queryParams.filter_date_to = this.filter_form.filter_date_to;
      }
    }

    this.router.navigate(["/pages/payment-collections"], {
      queryParams,
    });
  }


    getMonthlyPaymentsReport(is_export?) {
      if (is_export) {
        this.downloading_export = 1;
        this.filter_form.is_export = 1;
      } else {
        this.enableLoading();
      }
      this.financialReportsService
        .getMonthlyPaymentsReport(this.filter_form)
        .subscribe(
          (res: any) => {
            if (is_export) {
              window.open(res.url);
              this.downloading_export = false;
              this.filter_form.is_export = false;
              delete this.filter_form.type;
            } else {
              this.reports.monthly_payments_analysis_total = res.total_for_total;
              res = Object.entries(res).map(([key, value]) => {
                if (key == "total_for_total") return;
                let data = [];
                Object.entries(value).map(([key2, value2]) => {
                  data.push({ value: value2 });
                });
                return {
                  year: key,
                  data: data,
                };
              });
              this.reports.monthly_payments_analysis = res;
              this.reports.monthly_payments_analysis = removeNullishFieldsParams(
                this.reports.monthly_payments_analysis
              );
              console.log(this.reports.monthly_payments_analysis);
              console.log(this.monthsArray);
            }
          },
          (err) => {
            this.downloading_export = false;
            console.log(err);
          },
          () => {
            if (this.view === "monthly_payments_analysis") {
              this.disableLoading();
            }
          }
        );
    }
    getPartnerMonthlyPaymentsReport(is_export?) {
      if (is_export) {
        this.downloading_export = 1;
        this.filter_form.is_export = 1;
      } else {
        this.enableLoading();
      }
      this.financialReportsService
        .getPartnerMonthlyPaymentsReport(this.filter_form)
        .subscribe(
          (res: any) => {
            if (is_export) {
              window.open(res.url);
              this.downloading_export = false;
              this.filter_form.is_export = false;
              delete this.filter_form.type;
            } else {
              this.reports.partner_monthly_payments_analysis_total =
                res.total_for_total;
              res = Object.entries(res).map(([key, value]) => {
                if (key == "total_for_total") return;
                let data = [];
                Object.entries(value).map(([key2, value2]) => {
                  data.push({ value: value2 });
                });
                return {
                  year: key,
                  data: data,
                };
              });
              this.reports.partner_monthly_payments_analysis = res;
              console.log(this.reports.partner_monthly_payments_analysis);
              console.log(this.monthsArray);
            }
          },
          (err) => {
            this.downloading_export = false;
            console.log(err);
          },
          () => {
            if (this.view === "partner_monthly_payments_analysis") {
              this.disableLoading();
            }
          }
        );
    }

  getInvestmentReport(is_export?) {
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService
      .getProjectUnitMetrics(this.filter_form)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
          } else {
            // Store the raw response to see the structure
            this.reports.investment.raw_response = res;
            this.reports.investment.data = Array.isArray(res) ? res : (res.data || []);

            // Calculate totals
            this.calculateInvestmentTotals();

            // Prepare chart data
            this.prepareInvestmentChartData();

            console.log('Investment Report Response:', res);
          }
        },
        (err) => {
          this.downloading_export = false;
          this.errorHandlerService.handleErorr(err);
          console.log('Investment Report Error:', err);
        },
        () => {
          if (this.view === "investment") {
            this.disableLoading();
          }
        }
      );
  }

  calculateInvestmentTotals() {
    if (!this.reports.investment.data || !Array.isArray(this.reports.investment.data)) {
      return;
    }

    const totals = {
      total_count: 0,
      total_area: 0,
      total_amount: 0,
      total_average_price_per_area: 0,
      reserved_count: 0,
      reserved_area: 0,
      reserved_amount: 0,
      reserved_average_price_per_area: 0,
      reserved_percentage: 0,
      available_count: 0,
      available_area: 0,
      available_amount: 0,
      available_average_price_per_area: 0,
    };

    this.reports.investment.data.forEach(item => {
      // Total Units
      totals.total_count += (item.total && item.total.count) || 0;
      totals.total_area += (item.total && item.total.total_build_area) || 0;
      totals.total_amount += (item.total && item.total.total_fully) || 0;
      totals.total_average_price_per_area += (item.total && item.total.average_price_per_area) || 0;

      // Reserved (Sold) Units
      totals.reserved_count += (item.reserved && item.reserved.count) || 0;
      totals.reserved_area += (item.reserved && item.reserved.total_build_area) || 0;
      totals.reserved_amount += (item.reserved && item.reserved.total_fully) || 0;
      totals.reserved_average_price_per_area += (item.reserved && item.reserved.average_price_per_area) || 0;

      // Available (Inventory) Units
      totals.available_count += (item.available && item.available.count) || 0;
      totals.available_area += (item.available && item.available.total_build_area) || 0;
      totals.available_amount += (item.available && item.available.total_fully) || 0;
      totals.available_average_price_per_area += (item.available && item.available.average_price_per_area) || 0;
    });

    // Calculate reserved_percentage: (sum of reserved avg prices / sum of total avg prices) * 100
    if (totals.total_area > 0) {
      const percentage = (totals.reserved_area / totals.total_area) * 100;
      totals.reserved_percentage = percentage;
    } else {
      totals.reserved_percentage = 0;
    }

    if (totals.total_average_price_per_area) {
      totals.total_average_price_per_area = totals.total_average_price_per_area / this.reports.investment.data.length;
    }

    if (totals.reserved_average_price_per_area) {
      totals.reserved_average_price_per_area = totals.reserved_average_price_per_area / this.reports.investment.data.length;
    }

    if (totals.available_average_price_per_area) {
      totals.available_average_price_per_area = totals.available_average_price_per_area / this.reports.investment.data.length;
    }

    this.reports.investment.totals = totals;
    console.log('Final totals object:', this.reports.investment.totals);
  }

  prepareInvestmentChartData() {
    if (!this.reports.investment.data || !Array.isArray(this.reports.investment.data)) {
      return;
    }

    // Define colors for the pie chart
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF6384',
      '#36A2EB',
      '#FFCE56'
    ];

    // Clear existing data
    this.investmentChartData.labels = [];
    this.investmentChartData.datasets[0].data = [];
    this.investmentChartData.datasets[0].backgroundColor = [];
    this.investmentChartData.datasets[0].hoverBackgroundColor = [];

    // Populate chart data from investment report
    this.reports.investment.data.forEach((item, index) => {
      if (item.total && item.total.total_fully > 0) {
        this.investmentChartData.labels.push(item.project_name);
        this.investmentChartData.datasets[0].data.push(item.total.total_fully);
        const color = colors[index % colors.length];
        this.investmentChartData.datasets[0].backgroundColor.push(color);
        this.investmentChartData.datasets[0].hoverBackgroundColor.push(color);
      }
    });

    // Prepare bar chart data for gross area comparison
    this.investmentBarChartData.labels = [];
    this.investmentBarChartData.datasets[0].data = [];
    this.investmentBarChartData.datasets[1].data = [];

    this.reports.investment.data.forEach((item) => {
      if (item.project_name) {
        this.investmentBarChartData.labels.push(item.project_name);
        // Total gross area
        this.investmentBarChartData.datasets[0].data.push(
          (item.total && item.total.total_build_area) || 0
        );
        // Sold units gross area (reserved)
        this.investmentBarChartData.datasets[1].data.push(
          (item.reserved && item.reserved.total_build_area) || 0
        );
      }
    });

    // Prepare Sold vs Inventory Gross Area Pie Chart
    if (this.reports.investment.totals) {
      const totals = this.reports.investment.totals;
      const soldGrossArea = totals.reserved_area || 0;
      const inventoryGrossArea = totals.available_area || 0;

      this.investmentGrossAreaPieChartData.datasets[0].data = [soldGrossArea, inventoryGrossArea];
    }

    // Trigger change detection for chart update
    this.investmentChartData = {...this.investmentChartData};
    this.investmentBarChartData = {...this.investmentBarChartData};
    this.investmentGrossAreaPieChartData = {...this.investmentGrossAreaPieChartData};
  }

  prepareDeveloperPercentageCharts() {
    if (!this.reports.developer_percentage.data || !Array.isArray(this.reports.developer_percentage.data)) {
      return;
    }

    // Define colors for the pie chart
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF6384',
      '#36A2EB',
      '#FFCE56'
    ];

    // Clear existing data
    this.developerPieChartData.labels = [];
    this.developerPieChartData.datasets[0].data = [];
    this.developerPieChartData.datasets[0].backgroundColor = [];
    this.developerPieChartData.datasets[0].hoverBackgroundColor = [];

    this.developerBarChartData.labels = [];
    this.developerBarChartData.datasets[0].data = [];
    this.developerBarChartData.datasets[1].data = [];

    // Populate charts data
    this.reports.developer_percentage.data.forEach((item, index) => {
      if (item.project_name && item.collected_amount > 0) {
        // Pie chart data - collected values
        this.developerPieChartData.labels.push(item.project_name);
        this.developerPieChartData.datasets[0].data.push(item.collected_amount);
        const color = colors[index % colors.length];
        this.developerPieChartData.datasets[0].backgroundColor.push(color);
        this.developerPieChartData.datasets[0].hoverBackgroundColor.push(color);
      }

      if (item.project_name) {
        // Bar chart data - all vs collected
        this.developerBarChartData.labels.push(item.project_name);
        this.developerBarChartData.datasets[0].data.push(item.total_amount || 0);
        this.developerBarChartData.datasets[1].data.push(item.collected_amount || 0);
      }
    });

    // Prepare Overall Collected vs Remaining Pie Chart
    if (this.reports.developer_percentage.totals) {
      const totals = this.reports.developer_percentage.totals;
      const collectedAmount = totals.collected_amount || 0;
      const remainingAmount = totals.under_amount || 0;

      this.developerOverallPieChartData.datasets[0].data = [collectedAmount, remainingAmount];
    }

    // Trigger change detection for chart update
    this.developerPieChartData = {...this.developerPieChartData};
    this.developerBarChartData = {...this.developerBarChartData};
    this.developerOverallPieChartData = {...this.developerOverallPieChartData};
  }

  prepareInstallmentCharts() {
    if (!this.reports.installment.data || !Array.isArray(this.reports.installment.data)) {
      return;
    }

    // Define colors for the pie chart
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF6384',
      '#36A2EB',
      '#FFCE56'
    ];

    // Clear existing data
    this.installmentPieChartData.labels = [];
    this.installmentPieChartData.datasets[0].data = [];
    this.installmentPieChartData.datasets[0].backgroundColor = [];
    this.installmentPieChartData.datasets[0].hoverBackgroundColor = [];

    this.installmentBarChartData.labels = [];
    this.installmentBarChartData.datasets[0].data = [];
    this.installmentBarChartData.datasets[1].data = [];

    // Populate charts data
    this.reports.installment.data.forEach((item, index) => {
      if (item.project_name && item.collected_amount > 0) {
        // Pie chart data - collected values
        this.installmentPieChartData.labels.push(item.project_name);
        this.installmentPieChartData.datasets[0].data.push(item.collected_amount);
        const color = colors[index % colors.length];
        this.installmentPieChartData.datasets[0].backgroundColor.push(color);
        this.installmentPieChartData.datasets[0].hoverBackgroundColor.push(color);
      }

      if (item.project_name) {
        // Bar chart data - all vs collected
        this.installmentBarChartData.labels.push(item.project_name);
        this.installmentBarChartData.datasets[0].data.push(item.total_amount || 0);
        this.installmentBarChartData.datasets[1].data.push(item.collected_amount || 0);
      }
    });

    // Prepare Overall Collected vs Remaining Pie Chart
    if (this.reports.installment.totals) {
      const totals = this.reports.installment.totals;
      const collectedAmount = totals.collected_amount || 0;
      const remainingAmount = totals.under_amount || 0;

      this.installmentOverallPieChartData.datasets[0].data = [collectedAmount, remainingAmount];
    }

    // Trigger change detection for chart update
    this.installmentPieChartData = {...this.installmentPieChartData};
    this.installmentBarChartData = {...this.installmentBarChartData};
    this.installmentOverallPieChartData = {...this.installmentOverallPieChartData};
  }

  prepareSoldInventoryChart(isSpecial?: boolean) {
    const reportData = isSpecial ? this.reports.sold_inventorty_special.data : this.reports.sold_inventorty.data;
    if (!reportData || !Array.isArray(reportData)) {
      return;
    }

    // Define colors for the pie chart
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF6384',
      '#36A2EB',
      '#FFCE56'
    ];

    // Clear existing data
    this.soldInventoryPieChartData.labels = [];
    this.soldInventoryPieChartData.datasets[0].data = [];
    this.soldInventoryPieChartData.datasets[0].backgroundColor = [];
    this.soldInventoryPieChartData.datasets[0].hoverBackgroundColor = [];

    // Populate pie chart data with developer percentage values
    reportData.forEach((item, index) => {
      if (item.project_name && item.developer_price > 0) {
        this.soldInventoryPieChartData.labels.push(item.project_name);
        this.soldInventoryPieChartData.datasets[0].data.push(item.developer_price);
        const color = colors[index % colors.length];
        this.soldInventoryPieChartData.datasets[0].backgroundColor.push(color);
        this.soldInventoryPieChartData.datasets[0].hoverBackgroundColor.push(color);
      }
    });

    // Trigger change detection for chart update
    this.soldInventoryPieChartData = {...this.soldInventoryPieChartData};
  }

  calculatePercentage(reserved: number, total: number): number {
    if (!total || total === 0) return 0;
    return (reserved / total) * 100;
  }

  getDeveloperPercentageReport(is_export?) {
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService
      .getProjectPaymentsSummaryForDev(this.filter_form)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
          } else {
            this.reports.developer_percentage.raw_response = res;
            this.reports.developer_percentage.data = res.data || [];
            this.reports.developer_percentage.totals = res.totals || null;

            // Prepare charts data
            this.prepareDeveloperPercentageCharts();

            console.log('Developer Percentage Report Response:', res);
          }
        },
        (err) => {
          this.downloading_export = false;
          this.errorHandlerService.handleErorr(err);
          console.log('Developer Percentage Report Error:', err);
        },
        () => {
          if (this.view === "developer_percentage") {
            this.disableLoading();
          }
        }
      );
  }

  getInstallmentReport(is_export?) {
    if (is_export) {
      this.downloading_export = 1;
      this.filter_form.is_export = 1;
    } else {
      this.enableLoading();
    }
    this.ceoReportsService
      .getProjectPaymentsSummary(this.filter_form)
      .subscribe(
        (res: any) => {
          if (is_export) {
            window.open(res.url);
            this.downloading_export = false;
            this.filter_form.is_export = false;
            delete this.filter_form.type;
          } else {
            this.reports.installment.raw_response = res;
            this.reports.installment.data = res.data || [];
            this.reports.installment.totals = res.totals || null;

            // Prepare charts data
            this.prepareInstallmentCharts();

            console.log('Installment Report Response:', res);
          }
        },
        (err) => {
          this.downloading_export = false;
          this.errorHandlerService.handleErorr(err);
          console.log('Installment Report Error:', err);
        },
        () => {
          if (this.view === "installment") {
            this.disableLoading();
          }
        }
      );
  }

  getDivisionReport() {
    this.enableLoading();
    this.ceoReportsService
      .getReservedUnitsByDealType(this.filter_form)
      .subscribe(
        (res: any) => {
          this.reports.division_report = res;
          console.log('Division Report Response:', res);

          // Prepare charts data
          this.prepareDivisionCharts();
        },
        (err) => {
          this.errorHandlerService.handleErorr(err);
          console.log('Division Report Error:', err);
        },
        () => {
          if (this.view === "division_report") {
            this.disableLoading();
          }
        }
      );
  }

  prepareDivisionCharts() {
    if (!this.reports.division_report || !this.reports.division_report.projects) {
      return;
    }

    // Ensure all three datasets exist
    if (!this.divisionBarChartData.datasets || this.divisionBarChartData.datasets.length !== 3) {
      this.divisionBarChartData.datasets = [
        {
          label: 'Direct',
          data: [],
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Indirect',
          data: [],
          backgroundColor: 'rgba(255, 206, 86, 0.8)',
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1
        },
        {
          label: 'Total',
          data: [],
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ];
    }

    // Clear existing data
    this.divisionBarChartData.labels = [];
    this.divisionBarChartData.datasets[0].data = []; // Direct
    this.divisionBarChartData.datasets[1].data = []; // Indirect
    this.divisionBarChartData.datasets[2].data = []; // Total

    // Populate bar chart data for each project
    this.reports.division_report.projects.forEach((project) => {
      if (project.project_name) {
        this.divisionBarChartData.labels.push(project.project_name);
        const directPrice = (project.direct && project.direct.total_price) || 0;
        const indirectPrice = (project.in_direct && project.in_direct.total_price) || 0;
        const totalPrice = directPrice + indirectPrice; // Calculate total as sum of direct and indirect

        this.divisionBarChartData.datasets[0].data.push(directPrice);
        this.divisionBarChartData.datasets[1].data.push(indirectPrice);
        this.divisionBarChartData.datasets[2].data.push(totalPrice);
      }
    });

    // Prepare pie chart for overall Direct vs Indirect
    if (this.reports.division_report.total) {
      const directTotal = (this.reports.division_report.total.direct && this.reports.division_report.total.direct.total_price) || 0;
      const indirectTotal = (this.reports.division_report.total.in_direct && this.reports.division_report.total.in_direct.total_price) || 0;

      this.divisionPieChartData.datasets[0].data = [directTotal, indirectTotal];
    }

    // Log the datasets to debug
    console.log('Division Bar Chart Datasets before update:', this.divisionBarChartData.datasets);
    console.log('Number of datasets:', this.divisionBarChartData.datasets.length);

    // Check the actual data values
    this.divisionBarChartData.datasets.forEach((dataset, index) => {
      console.log(`Dataset ${index} (${dataset.label}):`, dataset.data);
    });

    // Create a completely new object to force Angular change detection
    const newChartData = {
      labels: [...this.divisionBarChartData.labels],
      datasets: [
        {
          label: 'Direct',
          data: [...this.divisionBarChartData.datasets[0].data],
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Indirect',
          data: [...this.divisionBarChartData.datasets[1].data],
          backgroundColor: 'rgba(255, 206, 86, 0.8)',
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1
        },
        {
          label: 'Total',
          data: [...this.divisionBarChartData.datasets[2].data],
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    this.divisionBarChartData = newChartData;

    console.log('Division Bar Chart Datasets after update:', this.divisionBarChartData.datasets);
    console.log('Final number of datasets:', this.divisionBarChartData.datasets.length);

    this.divisionPieChartData = {...this.divisionPieChartData};
  }

  // loading alg
  loading_texts = [
    "Initiating Report",
    "Calculate Data",
    "Grouping Results",
    "Loading",
  ];

  loader = {
    index: 0,
    dot: 0,
    maxDot: 3,
    text: this.loading_texts[0],
    interval: null,
  };
  enableLoading() {
    this.isLoading = true;

    this.loader.index = 0;
    this.loader.dot = 0;
    this.loader.text = this.loading_texts[0];

    this.loader.interval = setInterval(() => {
      if (!this.isLoading) return this.disableLoading();

      if (this.loader.dot < this.loader.maxDot) {
        this.loader.dot++;
        this.loader.text += ".";
      } else {
        this.loader.dot = 0;
        if (this.loader.index < this.loading_texts.length - 1) {
          this.loader.index++;
        } else {
          this.loader.index = 0;
        }
        this.loader.text = this.loading_texts[this.loader.index];
      }
    }, 1000);
  }

  disableLoading() {
    this.isLoading = false;
    clearInterval(this.loader.interval);
  }

  downloadReport(sub_type?) {
    switch (this.view) {
      case "sold_inventorty": {
        this.getSoldInventortyReport(1);
        break;
      }
      case "sold_inventorty_special": {
        this.getSoldInventortyReport(1, true);
        break;
      }
      case "collection_remaining_amount_balance": {
        this.getCollectionRemainingAmountBalanceReport(1);
        break;
      }
      case "collection_remaining_amount_balance_special": {
        this.getCollectionRemainingAmountBalanceReport(1, true);
        break;
      }
      case "avaliable_inventorty": {
        this.getAvaliableInventortyReport(1);
        break;
      }
      case "dues_payment_with_sharing": {
        this.getDuesPaymentWithSharingReport(1);
        break;
      }
      case "dues_inventorty": {
        this.getDuesInventoryReport(1);
        break;
      }
      case "not_avaliable_inventorty": {
        this.getNotAvaliableInventortyReport(1);
        break;
      }
      case "monthly_payments_analysis": {
        this.getMonthlyPaymentsReport(1);
        break;
      }
      case "partner_monthly_payments_analysis": {
        this.getPartnerMonthlyPaymentsReport(1);
        break;
      }
      case "investment": {
        this.getInvestmentReport(1);
        break;
      }
      case "developer_percentage": {
        this.getDeveloperPercentageReport(1);
        break;
      }
      case "installment": {
        this.getInstallmentReport(1);
        break;
      }
    }
  }

  // singleReportDownloadCombinedStatemenet(row) {
  //   console.log(row);
  //   this.ceoReportsService
  //     .downloadReportCombinedStatementAccount(row.id)
  //     .subscribe((res: any) => {
  //       window.open(res);
  //     });
  // }
}



// WEBPACK FOOTER //
// ./src/app/ceo-reports/ceo-reports.component.ts